/* Course enrolment form, shared by /educations and the advanced course page.

   Sends through EmailJS with the same service, template and public key as the
   appointment form on /contact - see src/config/emailjs.js.

   It uses emailjs.send() rather than sendForm() so the payload maps explicitly
   onto the template's variables: the chosen course goes into `service`, which
   is where the template expects the requested service.

   Every field maps to exactly one template variable. Do not also repeat a
   field inside `message` - the template prints from_name, from_email, phone
   and service itself, so anything duplicated there shows up twice in the
   inbox.

   The five qualifying questions (experience, how long, Instagram, what is
   hardest, what she wants to improve) have no variables of their own in the
   EmailJS template, so they are folded into `message` as labelled lines. They
   are all optional: an unanswered question contributes no line at all, and a
   form where only the message was written still arrives as just that message.

   Traffic lands here from Google, so there is deliberately no step that leaves
   the site: the form submits in place and the only alternative offered is the
   phone number. */

import emailjs from 'emailjs-com';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SERVICE_ID, TEMPLATE_ID, PUBLIC_KEY, PHONE_DISPLAY, PHONE_TEL } from '../../config/emailjs';
import { BASIC_COURSE } from '../../config/educations.mjs';
import { BRAND, GOLD, INK, BODY, CREAM, serif, sans, buttonStyle } from '../ui/blocks';

/* Values sent to EmailJS stay Serbian in every language, so the salon always
   reads the same wording in the inbox. Only the labels are translated. */
const COURSE_VALUES = {
  basic: 'Bazni kurs šminkanja',
  advanced: 'Napredni kurs šminkanja',
};

const fieldStyle = {
  width: '100%',
  padding: '15px 20px',
  borderRadius: '14px',
  border: '1.5px solid rgba(144,81,15,0.18)',
  fontFamily: sans,
  fontSize: '14px',
  color: INK,
  background: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
};

const focusStyle = {
  borderColor: 'rgba(144,81,15,0.50)',
  boxShadow: '0 0 0 3.5px rgba(144,81,15,0.09)',
};

const Field = ({ as = 'input', children, style, ...props }) => {
  const [focused, setFocused] = useState(false);
  const Tag = as;
  return (
    <Tag
      {...props}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{ ...fieldStyle, ...(focused ? focusStyle : {}), ...style }}
    >
      {children}
    </Tag>
  );
};

const Label = ({ htmlFor, children }) => (
  <label
    htmlFor={htmlFor}
    style={{
      display: 'block',
      marginBottom: '8px',
      fontFamily: sans,
      fontSize: '13px',
      fontWeight: 600,
      color: INK,
    }}
  >
    {children}
  </label>
);

/** Placeholder so the message line is never blank in the email. */
const NO_MESSAGE = 'Bez poruke';

/* The labels the answers carry into the inbox, Serbian like COURSE_VALUES
   above, in the order the questions are asked on the page. */
const ANSWER_LABELS = {
  experience: 'Do sada radila šminku',
  experienceLength: 'Koliko dugo radi',
  instagram: 'Instagram profil',
  struggle: 'Najviše je muči',
  improve: 'Želi da unapredi',
};

const EXPERIENCE_VALUES = { yes: 'Da', no: 'Ne' };

/** The answered questions as labelled lines, plus the message under them. */
const composeMessage = (values) => {
  const lines = Object.keys(ANSWER_LABELS)
    .map((key) => {
      const answer = key === 'experience'
        ? EXPERIENCE_VALUES[values.experience]
        : values[key].trim();
      return answer ? `${ANSWER_LABELS[key]}: ${answer}` : null;
    })
    .filter(Boolean);

  const message = values.message.trim();
  if (!lines.length) return message || NO_MESSAGE;
  return message ? `${lines.join('\n')}\n\nPoruka: ${message}` : lines.join('\n');
};

const CourseSignupForm = ({ defaultCourse = 'basic' }) => {
  const { t, i18n } = useTranslation();
  const lang = ['sr', 'en', 'de'].includes(i18n.language) ? i18n.language : 'sr';

  const [values, setValues] = useState({
    name: '',
    phone: '',
    email: '',
    course: defaultCourse,
    experience: '',
    experienceLength: '',
    instagram: '',
    struggle: '',
    improve: '',
    message: '',
  });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const set = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');

    emailjs
      .send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          from_name: values.name.trim(),
          from_email: values.email.trim(),
          phone: values.phone.trim(),
          service: COURSE_VALUES[values.course],
          message: composeMessage(values),
        },
        PUBLIC_KEY
      )
      .then(
        () => setStatus('sent'),
        (error) => {
          console.error(error);
          setStatus('error');
        }
      );
  };

  const seats = t('courseForm.seats', {
    max: BASIC_COURSE.maxAttendees,
    startDate: BASIC_COURSE.startDateInline[lang] || BASIC_COURSE.startDateInline.sr,
  });

  return (
    <section id="prijava" style={{ padding: '72px 0', background: CREAM }}>
      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <p
            style={{
              fontFamily: sans,
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: GOLD,
              marginBottom: '10px',
            }}
          >
            {t('courseForm.eyebrow')}
          </p>
          <h2
            style={{
              fontFamily: serif,
              fontSize: 'clamp(1.75rem, 3.4vw, 2.5rem)',
              fontWeight: 600,
              color: INK,
              margin: '0 0 16px',
              lineHeight: 1.2,
            }}
          >
            {t('courseForm.heading')}
          </h2>
          <p
            style={{
              display: 'inline-block',
              fontFamily: sans,
              fontSize: '15px',
              fontWeight: 600,
              color: BRAND,
              background: 'rgba(144,81,15,0.09)',
              borderRadius: '9999px',
              padding: '10px 22px',
              margin: 0,
            }}
          >
            {seats}
          </p>
        </div>

        <div
          style={{
            background: '#fff',
            borderRadius: '28px',
            padding: 'clamp(24px, 5vw, 44px)',
            boxShadow: '0 24px 72px rgba(0,0,0,0.08)',
            border: '1px solid rgba(144,81,15,0.10)',
          }}
        >
          {status === 'sent' ? (
            /* An explicit confirmation panel - the fields disappearing is not
               enough of a signal that the application actually went through. */
            <div role="status" style={{ textAlign: 'center', padding: '20px 0' }}>
              <div
                style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  background: 'rgba(144,81,15,0.10)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={BRAND} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h3
                style={{
                  fontFamily: serif,
                  fontSize: '26px',
                  fontWeight: 600,
                  color: INK,
                  margin: '0 0 12px',
                }}
              >
                {t('courseForm.successTitle')}
              </h3>
              <p
                style={{
                  fontFamily: sans,
                  fontSize: '16px',
                  lineHeight: 1.8,
                  color: BODY,
                  margin: 0,
                }}
              >
                {t('courseForm.success')}
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate={false}>
              <div style={{ display: 'grid', gap: '18px' }}>
                <div>
                  <Label htmlFor="cf-name">{t('courseForm.fields.name')}</Label>
                  <Field id="cf-name" type="text" required value={values.name} onChange={set('name')} autoComplete="name" />
                </div>

                <div style={{ display: 'grid', gap: '18px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                  <div>
                    <Label htmlFor="cf-phone">{t('courseForm.fields.phone')}</Label>
                    <Field id="cf-phone" type="tel" required value={values.phone} onChange={set('phone')} autoComplete="tel" />
                  </div>
                  <div>
                    <Label htmlFor="cf-email">{t('courseForm.fields.email')}</Label>
                    <Field id="cf-email" type="email" required value={values.email} onChange={set('email')} autoComplete="email" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="cf-course">{t('courseForm.fields.courseLabel')}</Label>
                  <Field as="select" id="cf-course" required value={values.course} onChange={set('course')} style={{ cursor: 'pointer' }}>
                    <option value="basic">{t('courseForm.courses.basic')}</option>
                    <option value="advanced">{t('courseForm.courses.advanced')}</option>
                  </Field>
                </div>

                {/* The qualifying questions. All optional on purpose: the
                    form is the only conversion path on the page and a wall of
                    required fields costs more applications than the answers
                    are worth. */}
                <div>
                  <Label htmlFor="cf-experience">{t('courseForm.fields.experience')}</Label>
                  <Field as="select" id="cf-experience" value={values.experience} onChange={set('experience')} style={{ cursor: 'pointer' }}>
                    <option value="">{t('courseForm.fields.experiencePlaceholder')}</option>
                    <option value="yes">{t('courseForm.fields.experienceYes')}</option>
                    <option value="no">{t('courseForm.fields.experienceNo')}</option>
                  </Field>
                </div>

                <div style={{ display: 'grid', gap: '18px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                  <div>
                    <Label htmlFor="cf-experience-length">{t('courseForm.fields.experienceLength')}</Label>
                    <Field id="cf-experience-length" type="text" value={values.experienceLength} onChange={set('experienceLength')} />
                  </div>
                  <div>
                    <Label htmlFor="cf-instagram">{t('courseForm.fields.instagram')}</Label>
                    <Field id="cf-instagram" type="text" value={values.instagram} onChange={set('instagram')} />
                  </div>
                </div>

                <div>
                  <Label htmlFor="cf-struggle">{t('courseForm.fields.struggle')}</Label>
                  <Field as="textarea" id="cf-struggle" value={values.struggle} onChange={set('struggle')} style={{ height: '90px', resize: 'vertical' }} />
                </div>

                <div>
                  <Label htmlFor="cf-improve">{t('courseForm.fields.improve')}</Label>
                  <Field as="textarea" id="cf-improve" value={values.improve} onChange={set('improve')} style={{ height: '90px', resize: 'vertical' }} />
                </div>

                <div>
                  <Label htmlFor="cf-message">{t('courseForm.fields.message')}</Label>
                  <Field as="textarea" id="cf-message" value={values.message} onChange={set('message')} style={{ height: '130px', resize: 'vertical' }} />
                </div>

                {status === 'error' && (
                  <p role="alert" style={{ fontFamily: sans, fontSize: '14px', color: '#B3261E', margin: 0, lineHeight: 1.6 }}>
                    {t('courseForm.error')}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  style={{
                    ...buttonStyle('solid'),
                    width: '100%',
                    padding: '17px 36px',
                    opacity: status === 'sending' ? 0.6 : 1,
                    cursor: status === 'sending' ? 'wait' : 'pointer',
                  }}
                >
                  {t('courseForm.submit')}
                </button>
              </div>
            </form>
          )}

          <div style={{ marginTop: '28px', paddingTop: '24px', borderTop: '1px solid rgba(144,81,15,0.12)', textAlign: 'center' }}>
            <p style={{ fontFamily: sans, fontSize: '14px', color: BODY, margin: '0 0 12px' }}>
              {t('courseForm.phoneIntro')}
            </p>
            <a
              href={`tel:${PHONE_TEL}`}
              style={{
                fontFamily: serif,
                fontSize: '26px',
                fontWeight: 600,
                color: BRAND,
                textDecoration: 'none',
                letterSpacing: '0.01em',
              }}
            >
              {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CourseSignupForm;
